import { Inbox, SearchX } from "lucide-react";
import { useMemo } from "react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { useTranslations } from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OrganizationWebhookDelivery } from "../../services/organizationWebhooksService";
import { deliveryStatusVariant, formatLatency } from "../../utils/organizationWebhooks.utils";
import { deliveryStatusLabelKey, eventLabel, eventTypeLabel } from "./webhookLabels";

export interface WebhookDeliveriesTableProps {
  rows: OrganizationWebhookDelivery[];
  total: number;
  page: number;
  perPage: number;
  isLoading: boolean;
  hasActiveFilters: boolean;
  onPageChange: (page: number) => void;
  onOpen: (delivery: OrganizationWebhookDelivery) => void;
}

/**
 * One row per delivery, newest first (the backend orders by `created_at DESC`). Every column is something the
 * receiver's own logs would show — timestamp, event, outcome, HTTP code, attempts, latency — so a reader can
 * line the two up. Sits inside the section card, hence `frame="none"`.
 */
export function WebhookDeliveriesTable({
  rows,
  total,
  page,
  perPage,
  isLoading,
  hasActiveFilters,
  onPageChange,
  onOpen,
}: WebhookDeliveriesTableProps) {
  const { t } = useTranslations("settings");

  const columns = useMemo<ColocaDataTableColumn<OrganizationWebhookDelivery>[]>(
    () => [
      {
        id: "created_at",
        header: t("webhooks_th_created"),
        headerClassName: "w-[190px]",
        cell: (row) => (
          <span className="whitespace-nowrap text-[12.5px] text-foreground">
            {formatDateTime(row.created_at)}
          </span>
        ),
      },
      {
        id: "event",
        header: t("webhooks_th_event"),
        headerClassName: "w-[280px]",
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5">
            <ColocaBadge variant="secondary" className="shrink-0 text-[10px]">
              {eventTypeLabel(t, row.type)}
            </ColocaBadge>
            <span className="text-[12.5px] text-muted-foreground" title={row.event}>
              {eventLabel(t, row.event)}
            </span>
          </span>
        ),
      },
      {
        id: "status",
        header: t("webhooks_th_status"),
        headerClassName: "w-[140px]",
        cell: (row) => (
          <ColocaStatusPill variant={deliveryStatusVariant(row.status)} size="sm" withDot>
            {t(deliveryStatusLabelKey(row.status))}
          </ColocaStatusPill>
        ),
      },
      {
        id: "http",
        header: t("webhooks_th_http"),
        headerClassName: "w-[80px]",
        cellClassName: "font-mono text-[12.5px] tabular-nums",
        cell: (row) =>
          row.last_status_code != null ? (
            <span className={row.last_status_code >= 400 ? "text-destructive" : "text-foreground"}>
              {row.last_status_code}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "attempts",
        header: t("webhooks_th_attempts"),
        headerClassName: "w-[90px] text-right",
        cellClassName: "text-right font-mono text-[12.5px] tabular-nums",
        cell: (row) => row.attempt_count,
      },
      {
        id: "latency",
        header: t("webhooks_th_latency"),
        headerClassName: "w-[100px] text-right",
        cellClassName: "text-right font-mono text-[12.5px] tabular-nums text-muted-foreground",
        cell: (row) => formatLatency(row.response_time_ms) ?? "—",
      },
    ],
    [t],
  );

  return (
    <ColocaDataTable
      frame="none"
      minWidth={880}
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      onRowClick={onOpen}
      loading={isLoading}
      skeletonRows={perPage}
      empty={
        hasActiveFilters ? (
          <ColocaEmpty surface="card" icon={SearchX} title={t("webhooks_deliveries_empty_filtered")} />
        ) : (
          <ColocaEmpty
            surface="card"
            icon={Inbox}
            title={t("webhooks_deliveries_empty_title")}
            description={t("webhooks_deliveries_empty_desc")}
          />
        )
      }
      footer={
        total > perPage ? (
          <ColocaPagination
            page={page}
            perPage={perPage}
            total={total}
            onPageChange={onPageChange}
            withSeparator={false}
            size="sm"
          />
        ) : undefined
      }
    />
  );
}
